import { useRef } from 'react';

/**
 * Keeps one stable ref object per id across renders, so a child component
 * keyed by id (e.g. a featured nav item) always gets back the *same* ref
 * identity — needed by consumers that key imperative behavior off referential
 * stability rather than just id equality (e.g. MegaMenu's triggerRef, used for
 * positioning and outside-click detection).
 *
 * A ref is created once, the first render its id is seen, and never replaced
 * afterward — so adding a new id never disturbs any existing id's ref. An id
 * that later disappears from `ids` leaves its ref behind rather than being
 * pruned, matching the prior inline behavior this hook replaces.
 *
 * @param {Array<string|number>} ids - The ids to keep refs for.
 * @returns {React.MutableRefObject<Record<string|number, {current: HTMLElement|null}>>}
 *   A stable ref whose `.current` maps each id to its own ref object.
 */
const useRefMap = (ids) => {
	const refMap = useRef({});

	ids.forEach((id) => {
		if (!refMap.current[id]) {
			refMap.current[id] = { current: null };
		}
	});

	return refMap;
};

export default useRefMap;
